import { useMemo } from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useAtom } from "jotai";
import { productDbAtom } from "../../../features/products/product.state";
import {
  BundleComponent,
  getSlotStock,
  isBundle,
} from "../../../features/products/bundle";

interface BundleSlotPickerProps {
  slot: BundleComponent;
  /** Bundle Item being authored, so we don't list it as a possible component. */
  bundleItemId: string;
  onChange: (next: BundleComponent) => void;
  onRemove: () => void;
  /** Open the selected component product in a nested edit modal. */
  onEdit?: (componentItemId: string) => void;
}

type ProductOption = {
  id: string;
  label: string;
  code: string;
};

export const BundleSlotPicker = ({
  slot,
  bundleItemId,
  onChange,
  onRemove,
  onEdit,
}: BundleSlotPickerProps) => {
  const [productDb] = useAtom(productDbAtom);

  const options = useMemo<ProductOption[]>(() => {
    const result: ProductOption[] = [];
    productDb.productMap.forEach((ref) => {
      if (ref.product.id === bundleItemId) return;
      if (isBundle(ref.product)) return; // no nested bundles in Phase 1
      result.push({
        id: ref.product.id,
        label: ref.product.name || ref.product.code || "(untitled)",
        code: ref.product.code,
      });
    });
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [productDb.productMap, bundleItemId]);

  const selectedOption = options.find((o) => o.id === slot.itemId) ?? null;
  const selectedRef = slot.itemId
    ? productDb.productMap.get(slot.itemId)
    : undefined;
  const variants = selectedRef?.product.variants ?? [];

  const stock = useMemo(
    () => (slot.itemId ? getSlotStock(slot, productDb) : null),
    [slot, productDb],
  );

  const stockLabel = (() => {
    if (!stock) return "";
    if (stock.quantity !== null) return `Stock: ${stock.quantity}`;
    if (stock.status === "in") return "In stock";
    if (stock.status === "out") return "Out of stock";
    return "Stock not tracked";
  })();
  const stockColor =
    stock?.status === "in"
      ? "success.main"
      : stock?.status === "out"
        ? "warning.main"
        : "text.disabled";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "nowrap",
        mb: 1,
      }}
    >
      <Autocomplete
        sx={{ minWidth: 120, flex: 1 }}
        size="small"
        options={options}
        value={selectedOption}
        onChange={(_, next) =>
          onChange({
            ...slot,
            itemId: next?.id ?? "",
            variantId: 0,
          })
        }
        getOptionLabel={(o) => o.label}
        renderOption={(props, o) => (
          <li {...props} key={o.id}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <span>{o.label}</span>
              {o.code && (
                <small style={{ color: "#888" }}>{o.code}</small>
              )}
            </Box>
          </li>
        )}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        renderInput={(params) => (
          <TextField {...params} label="Product" placeholder="Search products" />
        )}
      />

      <TextField
        size="small"
        select
        label="Variant"
        sx={{ width: 150, flexShrink: 1, minWidth: 90 }}
        value={slot.variantId ?? 0}
        onChange={(e) =>
          onChange({ ...slot, variantId: Number(e.target.value) || 0 })
        }
        disabled={!selectedRef || variants.length === 0}
      >
        <MenuItem value={0}>
          {variants.length > 0
            ? "Customer picks at order"
            : "(whole product)"}
        </MenuItem>
        {variants.map((v) => {
          const label =
            v.options
              ?.map((o) => o.value)
              .filter((x) => x)
              .join(" / ") || v.sku || `#${v.id}`;
          return (
            <MenuItem value={v.id} key={v.id}>
              {label}
            </MenuItem>
          );
        })}
      </TextField>

      <TextField
        size="small"
        label="Qty"
        type="number"
        sx={{ width: 70, flexShrink: 0 }}
        value={slot.quantity}
        onChange={(e) =>
          onChange({
            ...slot,
            quantity: Math.max(1, Number(e.target.value) || 1),
          })
        }
        slotProps={{ htmlInput: { min: 1 } }}
      />

      {slot.itemId && (
        <Typography
          variant="caption"
          sx={{
            width: 90,
            flexShrink: 0,
            color: stockColor,
            whiteSpace: "nowrap",
          }}
        >
          {stockLabel}
        </Typography>
      )}

      {onEdit && (
        <IconButton
          size="small"
          onClick={() => slot.itemId && onEdit(slot.itemId)}
          disabled={!slot.itemId}
          aria-label="Edit component"
          title="Edit this component in a new modal"
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      )}

      <IconButton
        size="small"
        onClick={onRemove}
        aria-label="Remove slot"
        title="Remove slot"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
